// Copied from caloteam/services#v5.59.0 (ComponentService), reduced to the methods this app calls.
import { round, sum } from 'lodash-es';

import { CupSizes, MeasurementUnit } from '../types';

type ChildComponentWeightSource = {
  quantity?: number;
  cups?: CupSizes[];
  measurementUnit?: MeasurementUnit;
  weight?: number;
};

type ComponentWeightSource = {
  ingredients?: { quantity?: number; weight?: number }[];
  childComponents?: ChildComponentWeightSource[];
};

const calculateComponentWeightFromCups = (quantity: number, cups: CupSizes[]): number => {
  const orderedCupSizes = cups.map((cup) => cup.size);
  orderedCupSizes.sort();
  orderedCupSizes.reverse();
  const orderedCups: CupSizes[] = [];
  for (const cup of cups) {
    orderedCups[orderedCupSizes.indexOf(cup.size)] = cup;
  }
  let remainingQuantity = quantity;
  let cupsQuantity = 0;
  for (const cup of orderedCups) {
    const quotient = Math.floor(remainingQuantity / cup.size);
    remainingQuantity -= quotient * cup.size;
    cupsQuantity += quotient * cup.value;
  }
  return cupsQuantity;
};

const calculateComponentCupsQuantityFromWeight = (cups: CupSizes[], weight: number): number => {
  if (!cups || cups.length === 0) {
    throw new Error('No cups sizes provided');
  }
  cups.sort((a, b) => b.size - a.size);
  let totalQuantity = 0;
  let remainingCupsQuantity = weight;
  for (const cup of cups) {
    if (remainingCupsQuantity <= 0) {
      break;
    }
    const maxCups = Math.floor(remainingCupsQuantity / cup.value);
    remainingCupsQuantity -= maxCups * cup.value;
    totalQuantity += maxCups * cup.size;
  }
  return totalQuantity;
};

const getWeightFromAllCupsQuantities = (quantities: { component: number; food?: number }[], cups: CupSizes[]): number => {
  let result = 0;
  for (const quantity of quantities) {
    result += calculateComponentWeightFromCups(quantity.component, cups) * quantity.food!;
  }
  return result;
};

const calculateComponentWeight = (
  cups: CupSizes[] | undefined,
  measurementUnit: MeasurementUnit,
  componentWeight: number,
  quantity: number
): number => {
  const weight =
    measurementUnit === MeasurementUnit.cup && cups && cups.length > 0
      ? calculateComponentWeightFromCups(quantity, cups)
      : componentWeight * quantity;
  return round(weight, 6);
};

const calculateComponentQuantityFromWeight = (
  cups: CupSizes[] | undefined,
  measurementUnit: MeasurementUnit,
  componentWeight: number,
  totalWeight: number
): number => {
  if (measurementUnit === MeasurementUnit.cup && cups && cups.length > 0) {
    return calculateComponentCupsQuantityFromWeight(cups, totalWeight);
  }
  return totalWeight / componentWeight;
};

const calculateTotalComponentAbsoluteWeight = (component: ComponentWeightSource): number => {
  const totalIngredientsWeight = sum(component.ingredients!.map((ing) => ing.quantity! * ing.weight!));
  let totalChildComponentsWeight = 0;
  for (const child of component.childComponents ?? []) {
    totalChildComponentsWeight += calculateComponentWeight(child.cups, child.measurementUnit!, child.weight!, child.quantity!);
  }
  return sum([totalIngredientsWeight, totalChildComponentsWeight]);
};

export const ComponentService = {
  calculateComponentWeightFromCups,
  getWeightFromAllCupsQuantities,
  calculateComponentWeight,
  calculateComponentQuantityFromWeight,
  calculateTotalComponentAbsoluteWeight
};
